// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the icon of the site a link preview points at. Previews are stored without one, so the app
// asks Konvo's API once per site (GET /konvo/site-icon) and keeps the answer for the session.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {useEffect, useState} from 'react';

/** Answers by site: the icon's media proxy address, or null when the site has none. */
const known = new Map<string, string | null>();
const asking = new Map<string, Promise<string | null>>();

function siteOf(url: string | undefined): string | null {
	if (!url) return null;
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : null;
	} catch {
		return null;
	}
}

function askSiteIcon(site: string): Promise<string | null> {
	const pending = asking.get(site);
	if (pending) return pending;
	const ask = http
		.get<{icon_url?: string | null}>(Endpoints.KONVO_SITE_ICON, {query: {url: `${site}/`}, mode: 'silent'})
		.then((response) => {
			const icon = response.ok && typeof response.body?.icon_url === 'string' ? response.body.icon_url : null;
			// A failed request is asked again next time; a site without an icon is not.
			if (response.ok) known.set(site, icon);
			return icon;
		})
		.catch(() => null)
		.finally(() => asking.delete(site));
	asking.set(site, ask);
	return ask;
}

/** The icon of the site a page is on, once known; null until then and for sites without one. */
export function useSiteIcon(url: string | undefined): string | null {
	const site = siteOf(url);
	const [icon, setIcon] = useState<string | null>(() => (site ? (known.get(site) ?? null) : null));
	useEffect(() => {
		if (!site) {
			setIcon(null);
			return;
		}
		if (known.has(site)) {
			setIcon(known.get(site) ?? null);
			return;
		}
		let live = true;
		void askSiteIcon(site).then((value) => {
			if (live) setIcon(value);
		});
		return () => {
			live = false;
		};
	}, [site]);
	return icon;
}
